/**
 * DdtSequenceDialog — controllo del progressivo dei numeri DDT (Rif. DDT):
 * evidenzia i numeri mancanti nella numerazione e i DDT allegati a più ordini.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ListChecks, AlertTriangle, CheckCircle2, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { API } from "../utils/format";

const fmtDate = (iso) => (iso ? new Date(iso.length === 10 ? iso + "T00:00:00" : iso).toLocaleDateString("it-IT") : "—");

const RangeBadge = ({ r }) => (
  <Badge variant="outline" className="font-mono text-amber-800 border-amber-300 bg-amber-50">
    {r[0] === r[1] ? r[0] : `${r[0]}–${r[1]}`}
  </Badge>
);

export default function DdtSequenceDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/ddt-sequence-check`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open]);

  const totMissing = (data?.years || []).reduce((s, y) => s + y.missing_count, 0);
  const totDup = (data?.years || []).reduce((s, y) => s + y.duplicates.length, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ddt-sequence-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-[#8A1C2A]" /> Controllo progressivo DDT
          </DialogTitle>
        </DialogHeader>

        {loading && <p className="p-6 text-center text-sm text-stone-500">Analisi delle numerazioni in corso…</p>}

        {!loading && !data && (
          <p className="p-6 text-center text-sm text-red-600">Errore nel caricamento del controllo</p>
        )}

        {!loading && data && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-3 rounded-lg border bg-stone-50 border-stone-200">
                <p className="text-xl font-bold text-stone-900" data-testid="ddtseq-total">{data.total_checked}</p>
                <p className="text-xs text-stone-500">DDT con numero</p>
              </div>
              <div className={`p-3 rounded-lg border ${totMissing > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                <p className={`text-xl font-bold ${totMissing > 0 ? "text-amber-800" : "text-emerald-700"}`} data-testid="ddtseq-missing">{totMissing}</p>
                <p className="text-xs text-stone-500">Numeri mancanti</p>
              </div>
              <div className={`p-3 rounded-lg border ${totDup > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                <p className={`text-xl font-bold ${totDup > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="ddtseq-duplicates">{totDup}</p>
                <p className="text-xs text-stone-500">DDT usati più volte</p>
              </div>
            </div>

            {data.years.length === 0 && (
              <p className="p-4 text-center text-sm text-stone-500" data-testid="ddtseq-empty">
                Nessun DDT con riferimento numerico trovato nei documenti
              </p>
            )}

            {data.years.map((y) => (
              <div key={y.year} className="border border-stone-200 rounded-lg p-3 space-y-2" data-testid={`ddtseq-year-${y.year}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-stone-900">Anno {y.year}</p>
                  <p className="text-xs text-stone-500">
                    dal n. <b className="font-mono">{y.min}</b> al n. <b className="font-mono">{y.max}</b> · {y.count} DDT registrati su {y.expected} attesi
                  </p>
                </div>

                {y.missing_count === 0 && y.duplicates.length === 0 && (
                  <p className="flex items-center gap-1.5 text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" /> Numerazione completa: nessun DDT mancante né duplicato
                  </p>
                )}

                {y.missing_count > 0 && (
                  <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid={`ddtseq-missing-${y.year}`}>
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5 mb-1.5">
                      <AlertTriangle className="w-4 h-4" /> {y.missing_count} numeri DDT mancanti nella sequenza
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {y.missing_ranges.map((r, i) => <RangeBadge key={i} r={r} />)}
                    </div>
                  </div>
                )}

                {y.duplicates.length > 0 && (
                  <div className="p-2.5 rounded-lg bg-red-50 border border-red-200" data-testid={`ddtseq-dup-${y.year}`}>
                    <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5 mb-1.5">
                      <Copy className="w-4 h-4" /> DDT usati su più documenti
                    </p>
                    <div className="space-y-2">
                      {y.duplicates.map((dup) => (
                        <div key={dup.number} className="bg-white rounded border border-red-100 p-2" data-testid={`ddtseq-dup-num-${dup.number}`}>
                          <p className="text-sm font-mono font-bold text-stone-900 flex items-center gap-2">
                            DDT n. {dup.number}
                            {dup.different_orders && (
                              <Badge className="bg-red-600 text-white hover:bg-red-600 text-[10px]">SU {dup.count} ORDINI DIVERSI</Badge>
                            )}
                          </p>
                          <div className="mt-1 space-y-0.5">
                            {dup.docs.map((doc, i) => (
                              <p key={i} className="text-xs text-stone-600">
                                <span className="font-mono font-semibold">{doc.doc_number}</span>
                                {doc.order_number ? <> · ordine <span className="font-mono">{doc.order_number}</span></> : null}
                                {" · "}{doc.customer_name || "—"} · {fmtDate(doc.doc_date)}
                                {" · Rif: "}<span className="font-mono">{doc.document_reference}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {data.no_number_count > 0 && (
              <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200 text-xs text-stone-600" data-testid="ddtseq-no-number">
                <b>{data.no_number_count}</b> documenti con Rif. DDT senza numero riconoscibile:{" "}
                {data.no_number.slice(0, 8).map((d) => d.document_reference).join(", ")}{data.no_number_count > 8 ? "…" : ""}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ddtseq-close">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
